import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * OTA Update hook using @capgo/capacitor-updater (self-hosted).
 * 
 * Listens for download progress, completion, and handles reloading the app.
 */
const useOtaUpdate = () => {
    const [downloadProgress, setDownloadProgress] = useState(null); // { percent: number, bytes: number, total: number }
    const [isDownloading, setIsDownloading] = useState(false);
    const [isReadyToRestart, setIsReadyToRestart] = useState(false);
    const [updateInfo, setUpdateInfo] = useState(null);

    useEffect(() => {
        // Only run on native platforms (iOS/Android)
        if (!Capacitor.isNativePlatform()) return;

        let CapacitorUpdater;

        const init = async () => {
            try {
                const module = await import('@capgo/capacitor-updater');
                CapacitorUpdater = module.CapacitorUpdater;

                // Notify the plugin that the current bundle loaded OK (prevents rollback)
                await CapacitorUpdater.notifyAppReady();
                console.log('[OTA] App ready, current bundle confirmed.');

                // Listen for update events
                CapacitorUpdater.addListener('updateAvailable', (info) => {
                    console.log('[OTA] Update available:', info);
                    setIsDownloading(true);
                    setUpdateInfo(info);
                });

                CapacitorUpdater.addListener('downloadProgress', (progress) => {
                    console.log('[OTA] Download progress:', progress);
                    setIsDownloading(true);
                    setDownloadProgress(progress);
                });

                CapacitorUpdater.addListener('downloadComplete', (info) => {
                    console.log('[OTA] Download complete:', info);
                    setIsDownloading(false);
                    setIsReadyToRestart(true);
                    setUpdateInfo(info);
                });

                CapacitorUpdater.addListener('downloadFailed', (info) => {
                    console.error('[OTA] Download failed:', info);
                    setIsDownloading(false);
                });

            } catch (err) {
                console.log('[OTA] Updater not available:', err.message);
            }
        };

        init();

        return () => {
            if (CapacitorUpdater) {
                CapacitorUpdater.removeAllListeners();
            }
        };
    }, []);

    const reloadApp = async () => {
        try {
            const module = await import('@capgo/capacitor-updater');
            if (module.CapacitorUpdater?.reload) {
                await module.CapacitorUpdater.reload();
            } else {
                window.location.reload();
            }
        } catch (e) {
            window.location.reload();
        }
    };

    return {
        isDownloading,
        downloadProgress,
        isReadyToRestart,
        updateInfo,
        reloadApp
    };
};

export default useOtaUpdate;
